/* eslint-disable max-lines */
import { Dayjs } from 'dayjs';
import {
  CodeValidStatus,
  RehearsalsTime,
  TimezoneValue,
  VenueInfo,
  DeAgendaDynamicFieldValue,
  CompressUrl,
  ColorType,
  CloudResourceType,
} from '.';

export const ACTIVITY_MAX_LAST_DAYS = 365;
export const ACTIVITY_MAX_PLACE_COUNT = 50;
export const MAX_ACTIVITY_COUNT = 200000;
export const CLOUD_GAME_INTERVAL = 30;

export const DEFAULT_TICKET_EXPIRED_HOURS = 12;

export const DEFAULT_PASSWORD = '******';

// 功能开关枚举
export const enum ActivityFeatureSwitch {
  BASIC_INFO_UPDATE = 'activity_update', // 当前活动下的基本信息能否更新
  ADD_VENUE = 'add_venue', // 当前活动下的是否能添加新的场地
  MSG_SIGN = 'msg_sign',
  WEIMEI_CONFIG = 'game_play_conf',
  LIVE_CONFIG = 'live_stream',
  STATISTIC = 'statistic',
  ACTIVITY_DATA = 'activity_data', // 活动流量
  PROP_MALL = 'prop_mall',
  PRIZE_MALL = 'prize_mall',
}

// 活动用途
export const enum ActivePurposeEnum {
  All = 0, // 全部
  Formal = 1, // 正式
  Qa = 2, // 测试/体验
}

export const ACTIVITY_USE_TABS = [
  {
    label: '全部',
    key: ActivePurposeEnum.All,
  },
  {
    label: '正式商务活动',
    key: ActivePurposeEnum.Formal,
  },
  {
    label: '测试/体验活动',
    key: ActivePurposeEnum.Qa,
  },
];

export enum ActivityStatus {
  CREATING = 1,
  WAITING_START,
  DOING,
  FINISHED,
}

// 活动的报名阶段（未报名、报名中、报名结束）
export enum RegisterStatus {
  WaitingStart,
  Doing,
  Finished,
}

export enum CloudGamingResourceStatus {
  NOT_STARTED = 1,
  IN_USE,
  NOT_USED,
}

export enum ActivityDetailSlugEnum {
  DETAIL = 'detail',
  VENUE = 'venue',
  PLAYER = 'player',
  NOTICE = 'notice',
  STATISTIC = 'statistic',
  SCHEDULE = 'schedule',
  MALL = 'mall',
  PROP_MALL = 'prop-mall',
  AWARD_MALL = 'prize-mall',
  TICKET_STYLE_SETTING = 'ticket-style',
  TICKET_SALE_SETTING = 'ticket-sale',
  DRESS_REHEARSAL = 'rehearsal',
  BULLET_CHAT = 'bulletchat',
  CONTENT_CHECK = 'anti-garbage',
  TICKET_NOTICE = 'ticket-notice',
  ROLESETTING = 'rolesetting',
  CLOUDGAME_LOGINPAGE = 'cloudgame_loginpage',
  CLOTHLOGO = 'cloth-logo',
  HAIR = 'hair',
  Feature = 'feature',
  WEIMEI_CONFIG = 'weimei-config',
  LIVE_CONFIG = 'live-config',
  REPORT = 'report',
  NPC = 'npc',
  DOC_LIB = 'doc-lib',
  CORPUS = 'corpus',
  IDENTITY_CONFIG = 'identity-config',
}

export enum CorporateSlug {
  BASIC = 'basic',
  VENUE = 'venue',
}

export type ActivityDetailSlug = `${ActivityDetailSlugEnum}`;

export type SpaceActivitySlug = Exclude<ActivityDetailSlug, 'rehearsal'>;

export type ActivityLogItem = {
  label: string;
  value: number | string;
};

export enum ActivityConfigType {
  CustomFirework = 'custom_firework',
  Guide = 'guide',
  ShareBackground = 'share_background',
  OpeningVideo = 'opening_video',
}

export enum ActivityConfigModuleId {
  CustomFirework = -1,
}

export type ActivityConfig = {
  type: ActivityConfigType;
  id?: number;
  module_id?: number;
  conf?: Record<string, any>;
};

export enum ResourceStatus {
  RELEASED = 1,
  UN_RELEASED = 2,
}

export enum CodeExpireRuleEnum {
  TwelveHoursAfterActivity = 1,
  ThreeDaysAfterJoinActivity,
}

// 功能开关
export type SwitchConfItem = {
  detail?: string;
  manage_default_status?: null | string;
  manage_permission?: null | string;
  status: boolean;
  type: number;
};

export enum MediaChannelEnum {
  YunXin = 1,
  ShengWang,
}

export enum UserActivityType {
  Manage = 'manage',
  Attend = 'attend',
}

export type TicketInfo = {
  // 门票有效期
  valid_time: string[];
  customized_url?: {
    name: string;
    url: string;
  };
  name?: string;
  rehearsals?: RehearsalsTime[];
  bg_img?: string;
};

export type RehearsalResources = {
  cloud_gaming_flag: boolean;
  cloud_gaming_resource: number;
  id: number;
  order: number;
};

export type UserActivity = {
  id: number;
  timezone: {
    offset: string;
    zoneId: string;
  };
  meeting_time: string[];
  type: UserActivityType;
  status: ActivityStatus;
  recording_url?: string;
  aid: string;
  sid: string;
  top_image: string;
  name: string;
  player_info?: {
    player_figure_id: number;
    name: string;
  };
  ticket_info: TicketInfo;
  code_valid_time_parse: string[];
  code_valid_status: CodeValidStatus;

  // 以下为梳理出来没有用到的我的活动部分的接口字段
  code: string;
  code_valid_time: string[];
  client_url: string;
  web_url: string;
  cloud_gaming_flag?: boolean;
  extend_dict?: {
    rehearsals: RehearsalResources[];
  };

  customized_name?: string;
};

// 概览数据
export type OverviewInfo = {
  official_input_number: number;
  self_register_number: number;
  real_login_number: number;
  general_info_status: boolean;
  activity_space_status: boolean;
  player_manage_status: boolean;
};

export type MeetingPlaceInfo = {
  scene_loading: CompressUrl;
  venue_list?: VenueInfo[];
};

export type MeetingTime = {
  id?: number[];
  date: string;
  time: string[];
};

export type PeriodsTime = {
  id?: number[];
  date: string;
  time: Dayjs[][];
  errorInfo?: string;
};
export enum AccountType {
  App = 'app',
  // urs
  Email = 'email',
  Google = 'google',
  Phone = 'phone',
  Wechat = 'wechat',
  // corp
  OpenId = 'openid',
  // rbac 邮箱
  Server = 'server',
}

export type AccountInfo = Partial<Record<AccountType, string>> & { code?: number | string }; // code 为手机的区号（如 +86）

export const PHONE_ACCOUNT_INFO_SHOW_SORT = [
  AccountType.App,
  AccountType.Phone,
  AccountType.OpenId,
  AccountType.Server,
  AccountType.Email,
  AccountType.Wechat,
  AccountType.Google,
];

export const EMAIL_ACCOUNT_INFO_SHOW_SORT = [
  AccountType.App,
  AccountType.OpenId,
  AccountType.Server,
  AccountType.Email,
  AccountType.Phone,
  AccountType.Wechat,
  AccountType.Google,
];

export type ActivityInfo = {
  close_date: null | string;
  description: null | string;
  end_date: null | string;
  id: number;
  limit_person: number;
  space_limit_person?: number;
  meeting_placeinfo: MeetingPlaceInfo;
  daily_time: string[];
  meeting_time: null | MeetingTime[];
  name: string;
  purpose: ActivePurposeEnum;
  notice: {
    plain: string;
    unity: string;
  };
  owner: string;
  start_date: null | string;
  status: ActivityStatus;
  apply_url: string;
  client_url: string;
  web_url: string;
  agenda?: DeAgendaDynamicFieldValue[];
  top_news?: string;
  // 是否开启云游戏标志位
  cloud_gaming_flag?: boolean;
  cloud_resource_type: CloudResourceType;
  cloud_resource: {
    id: string;
    cloud_resource_pool_id: string;
    version?: string;
    cloud_gaming_resource: CloudGamingResourceStatus;
    tidal_flag?: string;
    tidal_cloud_gaming_resource: any;
  };

  resource: ResourceStatus;
  cloud_gaming_conf?: {
    partition?: {
      region: string;
      num: number;
    }[];
    tidal_partition?: {
      region: string;
      num: number;
    }[];
    resource_request_time?: string[];
  };
  aid?: string;
  sid?: string;
  timezone?: TimezoneValue;
  media_channel: MediaChannelEnum;
  code_expire_rule?: CodeExpireRuleEnum;
  // 该活动是否公开
  public_flag?: boolean;
  extends_info: {
    public_conf?: {
      type: 'official' | 'custom';
      link?: string;
      url?: string;
    };
    space_limit_person?: number;
    space_time?: string[];
  };
  create_time: string;
  extend_dict?: {
    rehearsals: RehearsalResources[];
  };
  total_duration?: number;
  // 以下部分为后台管理返回特有
  real_register_count?: number;
  real_total_count?: number;
  sale_limit?: number;
  auto_release?: number;
  tidal_flag?: boolean; // 是否开启潮汐控制
  organizer_list?: AccountInfo[];
  system_support_list?: AccountInfo[];
  num_statistics?: {
    atmosphere?: number;
    limit_person: number;
  };
  customized_name?: string;
  required_field?: string[];
  // 活动号
  code: string;

  // 在活动主页没有登录的情况下会返回一部分活动报名相关的信息（这里的字段自由度高，不和报名相关的 ts 定义公用）
  register_info?: {
    email: string;
    name: string;
    review_flag: boolean;
    sale_time: string[];
  };
  activity_time_range?: string[];
  top_image?: string;
  // 是否开启云游戏快速登录
  fast_login_flag: boolean;
};
export enum BUSINESS_TYPE {
  // 文本
  TEXT = 'text',
  // 批量文本
  TEXT_BATCH = 'text_batch',
  // 图片
  IMAGE = 'image',
  // 昵称
  USER_PF_TEXT = 'user_pf_text',
  // 批量昵称
  USER_PF_TEXT_BATCH = 'user_pf_text_batch',
  // 二维码
  IMAGE_WX_QR_CODE = 'image_wx_qr_code',
}
export interface GarbageDetectionForm {
  request_business_type: BUSINESS_TYPE;
  content: string | { name: string; type: string; data: string }[] | string[];
  spam_level?: 0 | 1;
  need_detail?: boolean;
}

export interface GarbageDetectionResponse {
  code: number;
  detail: any;
  msg: string;
}

export const spaceActivityStatusFilter: Array<{
  value: ActivityStatus;
  text: string;
  filterValue: string;
}> = [
  {
    value: ActivityStatus.CREATING,
    text: '创建中',
    filterValue: 'TODO',
  },
  {
    value: ActivityStatus.WAITING_START,
    text: '未开始',
    filterValue: 'WAITING',
  },
  {
    value: ActivityStatus.DOING,
    text: '进行中',
    filterValue: 'DOING',
  },
  {
    value: ActivityStatus.FINISHED,
    text: '已结束',
    filterValue: 'FINISH',
  },
];

export const ActivityStatusLabel = {
  [ActivityStatus.FINISHED]: '已结束',
  [ActivityStatus.DOING]: '进行中',
  [ActivityStatus.WAITING_START]: '未开始',
  [ActivityStatus.CREATING]: '创建中',
};

export enum ActivityFilterValue {
  creating = 'TODO',
  waiting = 'WAITING',
  doing = 'DOING',
  finished = 'FINISH',
}

// 企业控制台 - 活动卡片
export type SpaceActivityItem = Pick<
  ActivityInfo,
  'aid' | 'status' | 'name' | 'start_date' | 'end_date' | 'timezone' | 'meeting_time'
> & {
  top_image: string;
};

export const activityStatusList: Array<{
  value: ActivityStatus;
  text: string;
  filterValue: string;
  color: ColorType;
  tagImg?: string;
}> = [
  {
    value: ActivityStatus.CREATING,
    text: '创建中',
    filterValue: 'TODO',
    color: 'yellow',
  },
  {
    value: ActivityStatus.WAITING_START,
    text: '未开始',
    filterValue: 'WAITING',
    color: 'blue',
    tagImg: 'https://static.fuxi.netease.com/yaotai/web/index/lhpwz2en_783919.png',
  },
  {
    value: ActivityStatus.DOING,
    text: '进行中',
    filterValue: 'DOING',
    color: 'green',
    tagImg: 'https://static.fuxi.netease.com/yaotai/web/index/lhpwzk1a_806766.png',
  },
  {
    value: ActivityStatus.FINISHED,
    text: '已结束',
    filterValue: 'FINISH',
    color: 'grey',
    tagImg: 'https://static.fuxi.netease.com/yaotai/web/index/lhpwzylo_825644.png',
  },
];
export enum CustomerServiceEnum {
  Netease = 'netease',
  Tencent = 'tencent',
}

export enum OemEnums {
  // 空间 oem
  OEM_SPACE_MASTER = 'oem_space_master',
  // 空间登录退出
  OEM_SPACE_EXIT_URL = 'oem_space_exit_url',
  // 空间 logo
  OEM_SPACE_LT_LOGO = 'oem_space_lt_logo',
  // 空间手册
  OEM_SPACE_MANUAL = 'oem_space_manual',
  // 空间客服
  OEM_SPACE_CS = 'oem_space_cs',
  // 活动 oem
  OEM_ACTIVITY_MASTER = 'oem_activity_master',
  // 登录左上角
  OEM_LOG_LT_LOGO = 'oem_log_lt_logo',
  // 活动登录退出
  OEM_ADMIN_BACKUP_URL = 'oem_admin_backup_url',
  // 客服
  OEM_LOG_CS_ENTER = 'oem_log_cs_enter',
  // 我的
  OEM_LOG_SELF_CENTER = 'oem_log_self_center',
  // 下载
  OEM_LOG_DOWNLOAD = 'oem_log_download',
  // 下载组
  DOWNLOAD_ALL = 'download_all',
  // 协议
  OEM_LOG_PROTOCOL = 'oem_log_protocol',
  // 手册
  OEM_ADMIN_MANUAL = 'oem_admin_manual',
  // 手册地址
  OEM_ADMIN_MANUAL_LINK = 'oem_admin_manual_link',
  // 反馈
  OEM_ADMIN_FEEDBACK = 'oem_admin_feedback',
  // 手机登录
  OEM_LOG_PHONE = 'oem_log_phone',
  // 类型
  OEM_LOG_PHONE_TYPE = 'oem_log_phone_type',
  // 邮箱登录
  OEM_LOG_EMAIL = 'oem_log_email',
  // 类型
  OEM_LOG_EMAIL_TYPE = 'oem_log_email_type',
  // 微信登录
  OEM_LOG_WECHAT = 'oem_log_wechat',
  // 谷歌
  OEM_LOG_GOOGLE = 'oem_log_google',
  // 短信通道组
  OEM_MSG_GROUP = 'oem_msg_group',
  // 服务器
  MAIL_HOST = 'mail_host',
  // 用户名
  MAIL_USER = 'mail_user',
  // 密码
  MAIL_PASSWD = 'mail_password',
  // 邮箱名
  MAIL_SENDER = 'mail_sender',
  // 标签
  OEM_ICON_LOGO_SHOW = 'oem_icon_logo_show',
  // 地址
  OEM_ICON_LOGO = 'oem_icon_logo',
  // 客服类型
  CUSTOMER_SERVICE_TYPE = 'customer_service_type',
  // 腾讯客服脚本
  TENCENT_SCRIPT = 'tencent_script',
  // 自定义腾讯客服 icon
  TENCENT_CUSTOMER_ICON = 'tencent_customer_icon',
}

export enum LoginTypeEnum {
  URS = 'urs',
  RBAC = 'rbac',
}

export const oemSettings = {
  [OemEnums.OEM_LOG_LT_LOGO]: false,
  [OemEnums.OEM_LOG_CS_ENTER]: false,
  [OemEnums.OEM_LOG_SELF_CENTER]: false,
  [OemEnums.OEM_ADMIN_MANUAL]: false,
  [OemEnums.OEM_ADMIN_FEEDBACK]: false,
  [OemEnums.OEM_LOG_PHONE_TYPE]: 'rbac',
  [OemEnums.OEM_LOG_EMAIL_TYPE]: 'rbac',
  [OemEnums.OEM_LOG_WECHAT]: false,
  [OemEnums.OEM_LOG_GOOGLE]: false,
};

export const defaultSettings = {
  [OemEnums.OEM_LOG_LT_LOGO]: true,
  [OemEnums.OEM_LOG_CS_ENTER]: true,
  [OemEnums.OEM_LOG_SELF_CENTER]: true,
  [OemEnums.OEM_ADMIN_MANUAL]: true,
  [OemEnums.OEM_ADMIN_FEEDBACK]: true,
  [OemEnums.OEM_LOG_PHONE_TYPE]: LoginTypeEnum.URS,
  [OemEnums.OEM_LOG_EMAIL_TYPE]: LoginTypeEnum.URS,
  [OemEnums.OEM_LOG_WECHAT]: true,
  [OemEnums.OEM_LOG_GOOGLE]: true,
};
export enum DownloadEnum {
  LogDownload = 'pc',
  LogDownloadAndroid = 'android',
  LogDownloadIos = 'ios',
}
